import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bird, Bug, Shield, Check, Phone, Clock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedCalculator({ source = "landing_page" }) {
  // Bird proofing state
  const [panels, setPanels] = useState("");
  const [storeys, setStoreys] = useState("single");
  const [pigeonDuration, setPigeonDuration] = useState("<6m");
  const [addGutterCleaning, setAddGutterCleaning] = useState(false);
  const [addPanelWash, setAddPanelWash] = useState(false);
  const [addPressureWash, setAddPressureWash] = useState(false);
  const [addBirdMite, setAddBirdMite] = useState(false);
  const [dontKnowPanels, setDontKnowPanels] = useState(false);

  // Pest control add-on
  const [addPestControl, setAddPestControl] = useState(false);
  const [propertyType, setPropertyType] = useState("house");
  const [bedrooms, setBedrooms] = useState("3");
  const [treatmentType, setTreatmentType] = useState("standard");
  const [serviceFrequency, setServiceFrequency] = useState("oneoff");
  const [rodentTreatment, setRodentTreatment] = useState(false);

  // Termite add-on
  const [addTermite, setAddTermite] = useState(false);

  // Form
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const panelsRef = useRef(null);

  // === BIRD PROOFING PRICING ===
  const panelCount = parseInt(panels) || 0;
  const hasBirdEstimate = panelCount >= 1;

  let meshTotal = 0;
  if (hasBirdEstimate) {
    const tier1Rate = 42, tier2Rate = 45.50, tier3Rate = 49;
    const nestingAdd = pigeonDuration === ">6m" ? 2 : 0;
    let baseTotal;
    if (panelCount <= 16) {
      baseTotal = panelCount * (tier1Rate + nestingAdd);
    } else if (panelCount <= 26) {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + ((panelCount - 16) * (tier2Rate + nestingAdd));
    } else {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + (10 * (tier2Rate + nestingAdd)) + ((panelCount - 26) * (tier3Rate + nestingAdd));
    }
    let storeyMultiplier = 1, storeyAddition = 0;
    if (storeys === "two") storeyMultiplier = 1.25;
    else if (storeys === "three") { storeyMultiplier = 1.4; storeyAddition = 400; }
    meshTotal = Math.max(550, Math.round((baseTotal * storeyMultiplier) + storeyAddition));
  }

  // Cleaning services: scale with panels + storey
  const storeyCleanMult = (storeys === "three" ? 1.40 : storeys === "two" ? 1.20 : 1);
  const storeyFlat = (storeys === "three" ? 80 : storeys === "two" ? 50 : 0);
  const panelWashLevy = (storeys === "three" ? 80 : storeys === "two" ? 50 : 0);
  const gutterCleaningPrice = hasBirdEstimate ? Math.round((60 + panelCount * 8) * storeyCleanMult + storeyFlat) : 0;
  const panelWashPrice = hasBirdEstimate ? Math.round((25 + panelCount * 5) * storeyCleanMult + storeyFlat + panelWashLevy) : 0;
  const pressureWashPrice = hasBirdEstimate ? Math.round((50 + panelCount * 9) * storeyCleanMult + storeyFlat) : 0;

  const selectedCleaningCount = [addGutterCleaning, addPanelWash, addPressureWash].filter(Boolean).length;
  const isCleaningBundle = selectedCleaningCount >= 2;
  const rawCleaningTotal = (addGutterCleaning ? gutterCleaningPrice : 0) + (addPanelWash ? panelWashPrice : 0) + (addPressureWash ? pressureWashPrice : 0);
  const cleaningBundleDiscount = isCleaningBundle ? Math.round(rawCleaningTotal * 0.20) : 0;
  const cleaningTotal = rawCleaningTotal - cleaningBundleDiscount;

  const birdMitePrice = addBirdMite ? 200 : 0;

  // === PEST CONTROL PRICING (always calculate for display) ===
  const pestBasePrices = {
    house: { standard: { "1": 165, "2": 175, "3": 198, "4": 220, "5": 250, "6": 285 }, comprehensive: { "1": 220, "2": 240, "3": 280, "4": 330, "5": 380, "6": 450 } },
    unit: { standard: { "1": 150, "2": 170, "3": 190, "4": 210, "5": 240, "6": 270 }, comprehensive: { "1": 195, "2": 220, "3": 250, "4": 290, "5": 330, "6": 380 } }
  };

  const rodentDisplayPrice = (treatmentType === "comprehensive" ? 120 : 85) + ((storeys === "two" || storeys === "three") ? 20 : 0);

  // Always calculate pest display price (regardless of checkbox)
  let pestDisplayPrice = pestBasePrices[propertyType]?.[treatmentType]?.[bedrooms] || 198;
  if (storeys === "two" || storeys === "three") pestDisplayPrice += 30;
  if (serviceFrequency === "sixmonthly") pestDisplayPrice = Math.round(pestDisplayPrice * 0.85);

  const pestPrice = addPestControl ? pestDisplayPrice : 0;
  const rodentPrice = addPestControl && rodentTreatment ? rodentDisplayPrice : 0;

  // === TERMITE PRICING (always calculate for display) ===
  let termiteDisplayPrice = 150;
  const bedroomNum = parseInt(bedrooms);
  if (bedroomNum >= 5) termiteDisplayPrice += 70;
  else if (bedroomNum >= 4) termiteDisplayPrice += 40;
  else if (bedroomNum >= 3) termiteDisplayPrice += 20;
  if (storeys === "two" || storeys === "three") termiteDisplayPrice += 60;

  const termitePrice = addTermite ? termiteDisplayPrice : 0;

  // === TOTALS ===
  const birdTotal = meshTotal + cleaningTotal + birdMitePrice;
  const pestTotal = pestPrice + rodentPrice;
  const grandTotal = birdTotal + pestTotal + termitePrice;
  const launchDiscount = Math.round(grandTotal * 0.10);
  const discountedTotal = grandTotal - launchDiscount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasBirdEstimate && !dontKnowPanels) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/solar-estimate`, {
        ...formData,
        panels: panelCount || 0, storeys, pigeonDuration,
        meshTotal, gutterCleaning: addGutterCleaning, gutterCleaningPrice, panelWash: addPanelWash, panelWashPrice, pressureWash: addPressureWash, pressureWashPrice, cleaningBundleDiscount,
        birdMiteAddon: addBirdMite, birdMitePrice,
        pestControlAddon: addPestControl,
        ...(addPestControl && { propertyType, bedrooms, treatmentType, serviceFrequency, rodentTreatment, pestPrice: pestPrice + rodentPrice }),
        termiteAddon: addTermite, termitePrice,
        estimatedTotal: dontKnowPanels ? 0 : discountedTotal,
        dontKnowPanels,
        source
      });
      setSubmitted(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch { toast.error("Failed to send. Please call us."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setPanels(""); setStoreys("single"); setPigeonDuration("<6m");
    setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); setAddBirdMite(false); setDontKnowPanels(false);
    setAddPestControl(false); setPropertyType("house"); setBedrooms("3");
    setTreatmentType("standard"); setServiceFrequency("oneoff"); setRodentTreatment(false);
    setAddTermite(false);
    setFormData({ name: "", phone: "", email: "", address: "" }); setSubmitted(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="unified-calculator">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#5BC0DE] to-[#3498aa] text-white p-4 flex items-center gap-3">
        <Bird className="w-6 h-6" />
        <h3 className="text-xl font-bold">Get Your Instant Quote</h3>
      </div>

      <div className="grid md:grid-cols-2">
        {/* LEFT: Inputs */}
        <div className="p-5 space-y-4">
          {/* Bird Proofing Section */}
          <div className="space-y-3">
            <h4 className="font-bold text-[#5BC0DE] flex items-center gap-2 text-sm uppercase tracking-wide">
              <Bird className="w-4 h-4" /> Solar Panel Bird Proofing
            </h4>
            <div>
              <Label className="font-medium mb-1.5 block text-sm">Number of solar panels</Label>
              <Input ref={panelsRef} type="number" min="1" value={panels} onChange={(e) => { setPanels(e.target.value); setDontKnowPanels(false); }} placeholder="e.g., 20" className="text-lg py-5" data-testid="panels-input" disabled={dontKnowPanels} />
              <button type="button" onClick={() => { setDontKnowPanels(!dontKnowPanels); if (!dontKnowPanels) setPanels(""); }} className={`mt-2 text-sm font-medium transition-colors ${dontKnowPanels ? 'text-[#7ABC32]' : 'text-[#5BC0DE] hover:text-[#3498aa]'}`} data-testid="dont-know-panels-btn">
                {dontKnowPanels ? "I know my panel count" : "I don't know my panel count"}
              </button>
            </div>

            <div>
              <Label className="font-medium mb-1.5 block text-sm">House height</Label>
              <div className="flex gap-1">
                {[["single", "1 Storey"], ["two", "2 Storey"], ["three", "3+"]].map(([val, label]) => (
                  <Button key={val} type="button" variant={storeys === val ? "default" : "outline"} size="sm"
                    className={`flex-1 ${storeys === val ? "bg-[#5BC0DE] hover:bg-[#4AA8C4]" : ""}`}
                    onClick={() => setStoreys(val)} data-testid={`storeys-${val}`}>{label}</Button>
                ))}
              </div>
              {storeys === "two" && <p className="text-xs text-slate-500 mt-1.5 italic">*2 technicians required — harness system & higher access</p>}
              {storeys === "three" && <p className="text-xs text-slate-500 mt-1.5 italic">*Boom lift hire, harness system & 2 technicians required</p>}
            </div>

            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3">
              <Label className="font-semibold mb-2 block text-sm text-amber-800">How long have pigeons been nesting?</Label>
              <div className="grid grid-cols-2 gap-2">
                {[["<6m", "Less than 6 months"], [">6m", "More than 6 months"]].map(([val, label]) => (
                  <Button key={val} type="button" variant={pigeonDuration === val ? "default" : "outline"} size="sm"
                    className={`w-full py-3 text-sm ${pigeonDuration === val ? "bg-[#5BC0DE] hover:bg-[#4AA8C4]" : "border-amber-300 hover:bg-amber-100"}`}
                    onClick={() => { setPigeonDuration(val); if (val === "<6m") { setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); } }} data-testid={`duration-${val}`}>{label}</Button>
                ))}
              </div>
              {pigeonDuration === ">6m" && <p className="text-xs text-amber-700 mt-2 font-medium">We recommend cleaning services for longer nesting periods</p>}
            </div>

            {/* Cleaning Services - 3 separate options */}
            {pigeonDuration === ">6m" && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-[#7ABC32] uppercase tracking-wide">Cleaning Services {isCleaningBundle && <span className="ml-1 bg-[#7ABC32] text-white px-1.5 py-0.5 rounded-full normal-case">Bundle — save 20%!</span>}</p>
                {selectedCleaningCount > 0 && selectedCleaningCount < 2 && (
                  <p className="text-xs text-amber-600 font-medium">Select 2+ for a 20% bundle discount</p>
                )}
                <div className="space-y-2">
                  {[
                    { key: "gutter", active: addGutterCleaning, toggle: setAddGutterCleaning, label: "Gutter Clean", price: gutterCleaningPrice, desc: "Full gutter flush", details: ["Remove all debris from gutters", "Flush & clear downpipes", "Dispose of waste material"] },
                    { key: "panel", active: addPanelWash, toggle: setAddPanelWash, label: "Panel Wash", price: panelWashPrice, desc: "Surface wash panels", details: ["Water-fed broom clean on panel tops", "Remove bird droppings & residue", "Restore panel efficiency"] },
                    { key: "pressure", active: addPressureWash, toggle: setAddPressureWash, label: "Pressure Wash", price: pressureWashPrice, desc: "Wash & sanitise", details: ["High-pressure wash around panels", "Sanitise & deodorise affected areas", "Remove staining from roof surface"] },
                  ].map(opt => (
                    <label key={opt.key} className={`block p-2.5 rounded-lg cursor-pointer border-2 transition-colors ${opt.active ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} data-testid={`cleaning-${opt.key}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={opt.active} onCheckedChange={opt.toggle} />
                        <span className="font-medium text-xs whitespace-nowrap">{opt.label}</span>
                        <span className="text-[10px] text-slate-500 hidden sm:inline">— {opt.desc}</span>
                        {hasBirdEstimate && <span className="text-[#7ABC32] font-bold text-xs ml-auto">${opt.price}</span>}
                      </div>
                      {opt.active && (
                        <ul className="mt-1.5 ml-7 space-y-0.5">
                          {opt.details.map((d, i) => <li key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />{d}</li>)}
                        </ul>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Bird Mite Treatment */}
            <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 transition-colors ${addBirdMite ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-[#E74C3C]/5 border-[#E74C3C]/30'}`}>
              <Checkbox checked={addBirdMite} onCheckedChange={setAddBirdMite} className="mt-0.5" />
              <div>
                <span className="font-medium text-sm">+ Bird Mite Treatment (+$200)</span>
                {addBirdMite && (
                  <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Roof void dusting treatment</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Specialist bird mite chemical (internal & external)</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Follow-up call after 1 week</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Free follow-up treatment if needed</li>
                  </ul>
                )}
              </div>
            </label>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-slate-200 pt-3" />

          {/* Pest Control Add-on */}
          <div className="space-y-3">
            <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 transition-colors ${addPestControl ? 'bg-[#E74C3C]/10 border-[#E74C3C]' : 'bg-slate-50 border-slate-200'}`}>
              <Checkbox checked={addPestControl} onCheckedChange={setAddPestControl} className="mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-[#E74C3C]" /> Add Pest Control
                  </span>
                  <span className="text-sm font-bold text-[#E74C3C]">+${pestDisplayPrice}{rodentTreatment ? ` + $${rodentDisplayPrice}` : ""}</span>
                </div>
                <span className="text-xs text-slate-500 block">Cockroaches, ants, spiders, silverfish & more</span>
              </div>
            </label>

            {addPestControl && (
              <div className="pl-3 border-l-2 border-[#E74C3C]/30 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-medium mb-1.5 block text-sm">Property</Label>
                    <div className="flex gap-1">
                      {[["house", "House"], ["unit", "Unit"]].map(([val, label]) => (
                        <Button key={val} type="button" variant={propertyType === val ? "default" : "outline"} size="sm"
                          className={`flex-1 ${propertyType === val ? "bg-[#E74C3C] hover:bg-[#c0392b]" : ""}`}
                          onClick={() => setPropertyType(val)}>{label}</Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="font-medium mb-1.5 block text-sm">Bedrooms</Label>
                    <div className="flex gap-1">
                      {["1","2","3","4","5","6"].map(n => (
                        <Button key={n} type="button" variant={bedrooms === n ? "default" : "outline"} size="sm"
                          className={`flex-1 px-0 ${bedrooms === n ? "bg-[#E74C3C] hover:bg-[#c0392b]" : ""}`}
                          onClick={() => setBedrooms(n)}>{n}</Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* House Height */}
                <div>
                  <Label className="font-medium mb-1.5 block text-sm">House Height</Label>
                  <div className="flex gap-1">
                    {[["single", "1 Storey"], ["two", "2 Storey"], ["three", "3+"]].map(([val, label]) => (
                      <Button key={val} type="button" variant={storeys === val ? "default" : "outline"} size="sm"
                        className={`flex-1 ${storeys === val ? "bg-[#E74C3C] hover:bg-[#c0392b]" : ""}`}
                        onClick={() => setStoreys(val)}>{label}</Button>
                    ))}
                  </div>
                </div>

                {/* Service Frequency */}
                <div>
                  <Label className="font-medium mb-1.5 block text-sm">Frequency</Label>
                  <div className="flex gap-1">
                    {[["sixmonthly", "6-Monthly"], ["oneoff", "Yearly"]].map(([val, label]) => (
                      <Button key={val} type="button" variant={serviceFrequency === val ? "default" : "outline"} size="sm"
                        className={`flex-1 ${serviceFrequency === val ? "bg-[#E74C3C] hover:bg-[#c0392b]" : ""}`}
                        onClick={() => setServiceFrequency(val)}>
                        {label} {val === "sixmonthly" && <span className="ml-1 text-[10px] bg-[#7ABC32] text-white px-1 rounded">Save 15%</span>}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Treatment Type */}
                <div>
                  <Label className="font-medium mb-1.5 block text-sm">Treatment</Label>
                  <div className="flex gap-1">
                    {[["standard", "Standard"], ["comprehensive", "Comprehensive"]].map(([val, label]) => (
                      <Button key={val} type="button" variant={treatmentType === val ? "default" : "outline"} size="sm"
                        className={`flex-1 ${treatmentType === val ? "bg-[#E74C3C] hover:bg-[#c0392b]" : ""}`}
                        onClick={() => setTreatmentType(val)}>{label}</Button>
                    ))}
                  </div>
                </div>

                {/* Correct treatment info */}
                <div className="bg-[#E74C3C]/5 border border-[#E74C3C]/20 rounded-lg p-2.5">
                  <p className="text-xs font-semibold text-[#E74C3C] mb-1">{treatmentType === "standard" ? "Standard" : "Comprehensive"} includes:</p>
                  <ul className="space-y-0.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />Internal treatment</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />External treatment</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />Harbourage areas (fridge, dishwasher, sinks, subfloor)</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />Cockroach gel bait application</li>
                    {treatmentType === "comprehensive" && (
                      <>
                        <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Fence / full perimeter treatment</li>
                        <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Roof void dusting</li>
                        <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />3x cockroach pheromone sticky traps</li>
                        <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Ant treatment included</li>
                      </>
                    )}
                    <li className="flex items-center gap-1.5"><Check className={`w-3 h-3 flex-shrink-0 ${treatmentType === "comprehensive" ? "text-[#7ABC32]" : "text-[#E74C3C]"}`} /><span className={treatmentType === "comprehensive" ? "text-[#7ABC32] font-medium" : "text-[#E74C3C] font-medium"}>{treatmentType === "standard" ? "3-month warranty" : "6-month warranty"}</span></li>
                  </ul>
                </div>

                <label className="flex items-start gap-2 p-2.5 bg-slate-100 rounded-lg cursor-pointer">
                  <Checkbox checked={rodentTreatment} onCheckedChange={setRodentTreatment} className="mt-0.5" />
                  <div>
                    <span className="font-medium text-sm">+ Rodent Treatment (+${rodentDisplayPrice})</span>
                    {rodentTreatment && (
                      <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                        <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Internal & external bait stations</li>
                        <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Entry point identification</li>
                      </ul>
                    )}
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Termite Add-on */}
          <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 transition-colors ${addTermite ? 'bg-[#D2691E]/10 border-[#D2691E]' : 'bg-slate-50 border-slate-200'}`}>
            <Checkbox checked={addTermite} onCheckedChange={setAddTermite} className="mt-0.5" />
            <div>
              <span className="font-bold text-sm flex items-center gap-1.5">
                <Bug className="w-4 h-4 text-[#D2691E]" /> Add Termite Inspection (+${termiteDisplayPrice})
              </span>
              {addTermite && (
                <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Full property termite inspection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Thermal imaging & moisture detection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Detailed report with photos</li>
                </ul>
              )}
            </div>
          </label>
        </div>

        {/* RIGHT: Price & Form */}
        <div className="bg-slate-50 p-5 border-t md:border-t-0 md:border-l">
          {!submitted ? (
            <>
              {/* Price Box */}
              <div className={`rounded-xl overflow-hidden mb-4 ${(hasBirdEstimate || dontKnowPanels) ? 'bg-[#5BC0DE]' : 'bg-slate-300'}`}>
                <div className={`px-4 py-2 flex items-center justify-between ${(hasBirdEstimate || dontKnowPanels) ? 'bg-[#3498aa]' : 'bg-slate-400'}`}>
                  <img src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png" alt="Pest Free" className="h-10" />
                  {hasBirdEstimate && <span className="text-amber-400 text-xs font-bold whitespace-nowrap">10% OFF LAUNCH SPECIAL</span>}
                </div>
                <div className="p-4 text-white text-center">
                  {dontKnowPanels ? (
                    <div>
                      <p className="text-lg font-bold mb-1">No worries!</p>
                      <p className="text-sm text-white/80">Tell us your details and we'll call you with a free quote</p>
                    </div>
                  ) : hasBirdEstimate ? (
                    <>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-lg text-white/50 line-through">${grandTotal}</span>
                        <span className="text-3xl font-bold">${discountedTotal}</span>
                      </div>
                      <p className="text-amber-400 font-semibold text-sm">Save ${launchDiscount} (10% off)</p>
                      <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>

                      {/* Price breakdown */}
                      <div className="mt-3 text-left text-xs space-y-1 border-t border-white/20 pt-2">
                        <div className="flex justify-between"><span>Solar proofing mesh ({panelCount} panels)</span><span><span className="line-through text-white/40 mr-1">${meshTotal}</span>${Math.round(meshTotal * 0.9)}</span></div>
                        {addGutterCleaning && <div className="flex justify-between"><span>Gutter clean</span><span>${gutterCleaningPrice}</span></div>}
                        {addPanelWash && <div className="flex justify-between"><span>Panel wash</span><span>${panelWashPrice}</span></div>}
                        {addPressureWash && <div className="flex justify-between"><span>Pressure wash</span><span>${pressureWashPrice}</span></div>}
                        {isCleaningBundle && <div className="flex justify-between text-[#7ABC32]"><span>Bundle discount</span><span>-${cleaningBundleDiscount}</span></div>}
                        {addBirdMite && <div className="flex justify-between"><span>Bird mite treatment</span><span><span className="line-through text-white/40 mr-1">$200</span>${Math.round(200 * 0.9)}</span></div>}
                        {addPestControl && <div className="flex justify-between"><span>Pest control ({treatmentType}{serviceFrequency === "sixmonthly" ? ", 6-mo" : ""})</span><span><span className="line-through text-white/40 mr-1">${pestPrice}</span>${Math.round(pestPrice * 0.9)}</span></div>}
                        {rodentTreatment && addPestControl && <div className="flex justify-between"><span>Rodent treatment</span><span><span className="line-through text-white/40 mr-1">${rodentPrice}</span>${Math.round(rodentPrice * 0.9)}</span></div>}
                        {addTermite && <div className="flex justify-between"><span>Termite inspection</span><span><span className="line-through text-white/40 mr-1">${termitePrice}</span>${Math.round(termitePrice * 0.9)}</span></div>}
                        <div className="text-white/50 text-[10px] pt-1 border-t border-white/10">Prices include GST</div>
                      </div>
                    </>
                  ) : (
                    <button type="button" onClick={() => { panelsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); panelsRef.current?.focus(); }}
                      className="text-2xl font-bold py-2 cursor-pointer hover:text-white/80 transition-colors" data-testid="enter-panels-btn">
                      Enter panels
                    </button>
                  )}
                </div>
              </div>

              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Name *" className="bg-white" data-testid="name-input" />
                  <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Phone *" className="bg-white" data-testid="phone-input" />
                </div>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Email *" className="bg-white" data-testid="email-input" />
                <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Full address *" rows={2} className="bg-white" data-testid="address-input" />
                <Button type="submit" disabled={(!hasBirdEstimate && !dontKnowPanels) || submitting} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-5 font-semibold" data-testid="submit-btn">
                  {submitting ? "Sending..." : dontKnowPanels ? "Request a Callback" : "Get My Quote Now"}
                </Button>
                <p className="text-center text-xs text-slate-500">Free quote in 60 seconds — no obligation</p>
              </form>

              {/* Callback */}
              <div className="mt-4 text-center p-3 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Prefer to talk?</p>
                <a href="tel:0737081422" className="flex items-center justify-center gap-2 text-[#7ABC32] font-bold text-lg hover:underline">
                  <Phone className="w-5 h-5" /> (07) 3708 1422
                </a>
              </div>
            </>
          ) : (
            <div className="text-center py-12" data-testid="success-message">
              <div className="w-16 h-16 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Quote Sent!</h3>
              <p className="text-slate-600 mb-2">We'll be in touch within the hour.</p>
              <p className="text-2xl font-bold text-[#5BC0DE] mb-4">${discountedTotal}</p>
              <Button onClick={reset} variant="outline" size="sm">Get Another Quote</Button>
              <div className="mt-6 p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">Need it sooner?</p>
                <a href="tel:0737081422" className="text-[#7ABC32] font-bold text-lg flex items-center justify-center gap-2 mt-1">
                  <Phone className="w-5 h-5" /> (07) 3708 1422
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
